// src/components/ProfileDropdown.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    FaSignOutAlt, FaCalculator, FaEnvelope, FaCamera, FaUserCircle,
    FaBug, FaBook, FaSave, FaEdit, FaTimes, FaTrashAlt, FaCheck,
    FaClock, FaInfoCircle
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../services/api';
import './ProfileDropdown.css';

// ─── Constantes ───────────────────────────────────────────────────────────────

const AVATAR_FALLBACK = (nome) =>
    `https://ui-avatars.com/api/?name=${encodeURIComponent(nome || 'User')}&background=101C5D&color=fff`;

const MAX_PHOTO_SIZE_MB = 2;
const ALLOWED_PHOTO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

// Opções de duração de sessão (em horas). Devem casar com o backend
// (DURACOES_PERMITIDAS em routesUsers.js). Nunca acima de 8h.
const SESSION_DURATION_OPTIONS = [2, 4, 6, 8];

// ─── Hook: detecta clique fora ────────────────────────────────────────────────

function useOnClickOutside(ref, handler) {
    useEffect(() => {
        const listener = (e) => {
            if (!ref.current || ref.current.contains(e.target)) return;
            handler(e);
        };
        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);
        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [ref, handler]);
}

// ─── Helper: avaliador matemático seguro (sem eval / new Function) ────────────

function safeEval(expr) {
    const tokens = expr.match(/(\d+\.?\d*|[+\-*/()])/g);
    if (!tokens) throw new Error('Expressão inválida');

    let pos = 0;

    const peek = () => tokens[pos];
    const consume = () => tokens[pos++];

    const parseNumber = () => {
        const t = consume();
        if (t === '(') {
            const val = parseExpr();
            if (consume() !== ')') throw new Error('Parêntese não fechado');
            return val;
        }
        const n = parseFloat(t);
        if (isNaN(n)) throw new Error(`Token inválido: ${t}`);
        return n;
    };

    const parseFactor = () => {
        if (peek() === '-') { consume(); return -parseFactor(); }
        return parseNumber();
    };

    const parseTerm = () => {
        let left = parseFactor();
        while (peek() === '*' || peek() === '/') {
            const op = consume();
            const right = parseFactor();
            if (op === '/' && right === 0) throw new Error('Divisão por zero');
            left = op === '*' ? left * right : left / right;
        }
        return left;
    };

    const parseExpr = () => {
        let left = parseTerm();
        while (peek() === '+' || peek() === '-') {
            const op = consume();
            left = op === '+' ? left + parseTerm() : left - parseTerm();
        }
        return left;
    };

    const result = parseExpr();
    if (pos !== tokens.length) throw new Error('Expressão mal formada');
    return result;
}

// ─── Sub-componente: Calculadora ──────────────────────────────────────────────

const Calculadora = ({ onClose }) => {
    const [display, setDisplay] = useState('0');
    const ref = useRef(null);
    useOnClickOutside(ref, onClose);

    const handleBtn = useCallback((value) => {
        setDisplay((prev) => {
            if (prev === 'Erro') return value === 'C' ? '0' : value;
            if (value === 'C') return '0';
            if (value === '=') {
                try {
                    const result = safeEval(prev);
                    return Number.isFinite(result)
                        ? parseFloat(result.toFixed(10)).toString()
                        : 'Erro';
                } catch {
                    return 'Erro';
                }
            }
            if (value === '+/-') {
                return prev.startsWith('-') ? prev.slice(1) : '-' + prev;
            }
            if (value === '%') {
                try { return String(safeEval(prev) / 100); } catch { return 'Erro'; }
            }
            return prev === '0' && !['.', '+', '-', '*', '/'].includes(value)
                ? value
                : prev + value;
        });
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (/^[0-9+\-*/.%]$/.test(e.key)) { e.preventDefault(); handleBtn(e.key); }
            else if (e.key === 'Enter' || e.key === '=') { e.preventDefault(); handleBtn('='); }
            else if (e.key === 'Escape') onClose();
            else if (e.key === 'Backspace') {
                setDisplay((p) => (p.length > 1 && p !== 'Erro' ? p.slice(0, -1) : '0'));
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [handleBtn, onClose]);

    const buttons = ['C', '+/-', '%', '/', '7', '8', '9', '*', '4', '5', '6', '-', '1', '2', '3', '+', '0', '.', '='];

    return (
        <div className="calc-overlay" role="dialog" aria-label="Calculadora" aria-modal="true">
            <div className="calc-modal" ref={ref}>
                <div className="calc-display" aria-live="polite">{display}</div>
                <div className="calc-grid">
                    {buttons.map((btn) => (
                        <button
                            key={btn}
                            onClick={() => handleBtn(btn)}
                            className={[
                                'calc-btn',
                                ['/', '*', '-', '+', 'C'].includes(btn) && 'calc-btn--op',
                                btn === '=' && 'calc-btn--eq',
                                btn === '0' && 'calc-btn--zero',
                            ].filter(Boolean).join(' ')}
                        >
                            {btn === '+/-' ? '±' : btn}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};


// ─── Sub-componente: Modal de Ajuste de Imagem ────────────────────────────────

const ImageAdjustModal = ({ image, onCancel, onConfirm, isUploading }) => {
    const [zoom, setZoom] = useState(1);
    
    return (
        <div className="pdd-crop-overlay" role="dialog" aria-modal="true">
            <div className="pdd-crop-modal">
                <div className="pdd-crop-modal__header">
                    <h3>Ajustar Foto de Perfil</h3>
                    <button onClick={onCancel} disabled={isUploading} aria-label="Fechar"><FaTimes /></button>
                </div>
                <div className="pdd-crop-container">
                    <div className="pdd-crop-mask">
                        <img 
                            src={image} 
                            alt="Preview do enquadramento" 
                            style={{ transform: `scale(${zoom})` }} 
                            className="pdd-crop-image"
                        />
                    </div>
                </div>
                <div className="pdd-crop-controls">
                    <label htmlFor="zoomControl">Zoom da Imagem</label>
                    <input 
                        id="zoomControl"
                        type="range" 
                        min="1" 
                        max="3" 
                        step="0.1" 
                        value={zoom} 
                        onChange={(e) => setZoom(e.target.value)}
                        disabled={isUploading}
                    />
                </div>
                <div className="pdd-crop-actions">
                    <button className="pdd-btn-secondary" onClick={onCancel} disabled={isUploading}>Cancelar</button>
                    <button className="pdd-btn-primary" onClick={onConfirm} disabled={isUploading}>
                        {isUploading ? 'Enviando...' : <><FaCheck /> Salvar Imagem</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Helper: validação de foto ────────────────────────────────────────────────

function validatePhoto(file) {
    if (!file) return 'Nenhum arquivo selecionado.';
    if (!ALLOWED_PHOTO_TYPES.includes(file.type))
        return 'Formato inválido. Use PNG, JPG ou WebP.';
    if (file.size > MAX_PHOTO_SIZE_MB * 1024 * 1024)
        return `A imagem deve ter menos de ${MAX_PHOTO_SIZE_MB}MB.`;
    return null;
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function ProfileDropdown({
    user,
    onLogout,
    onThemeUpdate,
    onPhotoUpdate,
    appVersion,
    onVersionUpdate,
}) {
    const [isOpen, setIsOpen]               = useState(false);
    const [isCalcOpen, setIsCalcOpen]       = useState(false);
    const [isEditingVer, setIsEditingVer]   = useState(false);
    const [isSessionOpen, setIsSessionOpen] = useState(false); // sanfona da seção Segurança
    const [versionInput, setVersionInput]   = useState(appVersion);
    const [isUploading, setIsUploading]     = useState(false);
    const [imgError, setImgError]           = useState(false); // NOVO: Controle de erro de imagem

    // Estados para o ajuste de imagem
    const [tempImage, setTempImage]         = useState(null);
    const [rawFile, setRawFile]             = useState(null);

    // Duração de sessão: valor atual salvo + estado de "salvando"
    const [sessionHours, setSessionHours]   = useState(user?.session_duration_hours || null);
    const [savingHours, setSavingHours]     = useState(null); // guarda a opção clicada durante o request

    const containerRef = useRef(null);
    const fileInputRef = useRef(null);
    const close = useCallback(() => setIsOpen(false), []);
    useOnClickOutside(containerRef, close);

    // A sanfona de "Segurança" começa sempre recolhida: quando o menu fecha,
    // resetamos, para não exibir as opções de cara no próximo abrir.
    useEffect(() => {
        if (!isOpen) setIsSessionOpen(false);
    }, [isOpen]);

    // Reseta o erro de imagem sempre que a prop foto_perfil mudar (ex: upload concluído)
    useEffect(() => {
        setImgError(false);
    }, [user.foto_perfil]);

    useEffect(() => {
        if (!isEditingVer) setVersionInput(appVersion);
    }, [appVersion, isEditingVer]);

    // Mantém o estado local em sincronia se a prop do usuário mudar
    useEffect(() => {
        setSessionHours(user?.session_duration_hours || null);
    }, [user?.session_duration_hours]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleTheme = (e) => onThemeUpdate(e.target.checked ? 'dark' : 'light');

    const handlePhotoSelect = (e) => {
        const file = e.target.files[0];
        const error = validatePhoto(file);
        
        if (error) { 
            toast.error(error); 
            e.target.value = ''; 
            return; 
        }

        setRawFile(file);
        const reader = new FileReader();
        reader.onload = () => setTempImage(reader.result);
        reader.readAsDataURL(file);
        
        e.target.value = '';
    };

    const handleUploadConfirm = async () => {
        setIsUploading(true);
        const formData = new FormData();
        formData.append('foto_perfil', rawFile);
        
        try {
            const { data } = await api.put('/api/usuarios/me/foto', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Foto de perfil atualizada!');
            onPhotoUpdate(data.foto_perfil_url);
            setTempImage(null);
            setRawFile(null);
            setIsOpen(false);
        } catch {
            toast.error('Erro ao enviar a foto. Tente novamente.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeletePhoto = async (e) => {
        e.stopPropagation();
        if (!window.confirm('Deseja realmente remover sua foto de perfil?')) return;
        
        setIsUploading(true);
        try {
            await api.delete('/api/usuarios/me/foto'); 
            toast.success('Foto removida com sucesso!');
            // Atualiza o parent com null explícito
            onPhotoUpdate(null);
            setIsOpen(false);
        } catch (error) {
            console.error("Erro ao deletar:", error);
            toast.error('Erro ao remover foto.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleVersionSave = async (e) => {
        e.stopPropagation();
        if (versionInput === appVersion) { setIsEditingVer(false); return; }
        try {
            await api.put('/api/system-config/version', { version: versionInput });
            toast.success('Versão do sistema atualizada!');
            onVersionUpdate(versionInput);
            setIsEditingVer(false);
        } catch {
            toast.error('Falha ao atualizar a versão.');
        }
    };

    const handleVersionCancel = (e) => {
        e.stopPropagation();
        setVersionInput(appVersion);
        setIsEditingVer(false);
    };

    const openCalc = useCallback((e) => {
        e.stopPropagation();
        setIsCalcOpen(true);
        setIsOpen(false);
    }, []);

    const handleSentryTest = useCallback((e) => {
    e.stopPropagation();
    setIsOpen(false);
    setTimeout(() => { throw new Error('Teste Sentry Frontend — ProfileDropdown'); }, 0);
}, []);

    // Salva a preferência de duração de sessão.
    // IMPORTANTE: a mudança vale a partir do PRÓXIMO login — a sessão atual
    // continua no prazo com que nasceu. Comunicamos isso claramente ao usuário.
    const handleSessionHours = async (horas, e) => {
        if (e) e.stopPropagation();
        if (savingHours !== null) return;         // evita cliques concorrentes
        if (horas === sessionHours) return;        // já é o valor atual, no-op

        setSavingHours(horas);
        try {
            const { data } = await api.put('/api/usuarios/me/session-duration', { horas });
            setSessionHours(data.session_duration_hours);
            toast.info(
                `Duração definida para ${data.session_duration_hours}h. ` +
                `Isso passa a valer no seu próximo login — a sessão atual continua normalmente.`,
                { autoClose: 6000 }
            );
        } catch (err) {
            const msg = err?.response?.data?.error || 'Não foi possível salvar a duração da sessão.';
            toast.error(msg);
        } finally {
            setSavingHours(null);
        }
    };

    // ── Lógica blindada de Avatar ────────────────────────────────────────────
    const hasValidString = user.foto_perfil && user.foto_perfil !== 'null' && user.foto_perfil.trim() !== '';
    const avatarSrc = (hasValidString && !imgError) 
        ? user.foto_perfil 
        : AVATAR_FALLBACK(user.nome);

    const firstName = (user?.nome || '').split(' ')[0];

    return (
        <div className="pdd-container" ref={containerRef}>

            {/* ── Trigger ── */}
            <button
                className="pdd-trigger"
                onClick={() => setIsOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={isOpen}
            >
                <img 
                    src={avatarSrc} 
                    alt="Avatar" 
                    className="pdd-trigger__avatar" 
                    onError={() => setImgError(true)} 
                />
                <span className="pdd-trigger__name">{firstName}</span>
                <span className={`pdd-trigger__arrow ${isOpen ? 'pdd-trigger__arrow--open' : ''}`}>▼</span>
            </button>

            {/* ── Dropdown ── */}
            {isOpen && (
                <div className="pdd-menu" role="menu">

                    {/* Cabeçalho com foto editável e botão de excluir */}
                    <div className="pdd-menu__header">
                        <div className="pdd-avatar-wrap">
                            {/* AQUI ESTAVA O PROBLEMA: Faltava o onError nesta imagem */}
                            <img 
                                src={avatarSrc} 
                                alt="Avatar" 
                                className="pdd-avatar-wrap__img" 
                                onError={() => setImgError(true)}
                            />
                            
                            <div className="pdd-avatar-actions">
                                <button
                                    className={`pdd-avatar-btn pdd-avatar-btn--cam ${isUploading ? 'loading' : ''}`}
                                    onClick={() => !isUploading && fileInputRef.current?.click()}
                                    title="Alterar foto de perfil"
                                    disabled={isUploading}
                                    aria-label="Alterar foto de perfil"
                                >
                                    {isUploading ? '…' : <FaCamera />}
                                </button>
                                
                                {/* Mostra o botão de lixeira apenas se o usuário tem uma foto válida */}
                                {(hasValidString && !imgError) && (
                                    <button 
                                        className="pdd-avatar-btn pdd-avatar-btn--del"
                                        onClick={handleDeletePhoto}
                                        title="Remover foto de perfil"
                                        disabled={isUploading}
                                        aria-label="Remover foto de perfil"
                                    >
                                        <FaTrashAlt />
                                    </button>
                                )}
                            </div>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={ALLOWED_PHOTO_TYPES.join(',')}
                                onChange={handlePhotoSelect}
                                style={{ display: 'none' }}
                            />
                        </div>
                        <div className="pdd-menu__user-info">
                            <strong>{user.nome}</strong>
                            <span>{user.email}</span>
                        </div>
                    </div>

                    {/* Scrollable body */}
                    <div className="pdd-menu__body">

                        <div className="pdd-section">
                            <Link to="/meu-perfil" className="pdd-item" role="menuitem" onClick={close}>
                                <FaUserCircle aria-hidden="true" /> <span>Meu Perfil</span>
                            </Link>
                        </div>

                        <div className="pdd-section">
                            <p className="pdd-section__label">Ferramentas Rápidas</p>
                            <div className="pdd-item" role="menuitem" onClick={openCalc}>
                                <FaCalculator aria-hidden="true" /> <span>Calculadora</span>
                            </div>
                            <a
                                href="https://mail.google.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="pdd-item"
                                role="menuitem"
                            >
                                <FaEnvelope aria-hidden="true" /> <span>Acessar Webmail</span>
                            </a>
                            <Link to="/base-conhecimento" className="pdd-item" role="menuitem" onClick={close}>
                                <FaBook aria-hidden="true" /> <span>Base de Conhecimento</span>
                            </Link>
                        </div>

                        {/* ── Segurança: duração da sessão (sanfona) ── */}
                        <div className="pdd-section">
                            <p className="pdd-section__label">Segurança</p>

                            {/* Cabeçalho clicável: recolhido por padrão, mostra o valor atual */}
                            <button
                                type="button"
                                className="pdd-session-header"
                                onClick={(e) => { e.stopPropagation(); setIsSessionOpen((v) => !v); }}
                                aria-expanded={isSessionOpen}
                                aria-controls="pdd-session-panel"
                            >
                                <span className="pdd-session-header__label">
                                    <FaClock aria-hidden="true" />
                                    <span>Manter sessão ativa por</span>
                                </span>
                                <span className="pdd-session-header__right">
                                    {sessionHours && (
                                        <span className="pdd-session-header__badge">{sessionHours}h</span>
                                    )}
                                    <span
                                        className={`pdd-session-header__chevron ${isSessionOpen ? 'pdd-session-header__chevron--open' : ''}`}
                                        aria-hidden="true"
                                    >▼</span>
                                </span>
                            </button>

                            {/* Painel sanfonado */}
                            <div
                                id="pdd-session-panel"
                                className={`pdd-session-collapse ${isSessionOpen ? 'pdd-session-collapse--open' : ''}`}
                            >
                                <div className="pdd-session-collapse__inner">
                                    <div className="pdd-session">
                                        <div className="pdd-session__options" role="group" aria-label="Duração da sessão">
                                            {SESSION_DURATION_OPTIONS.map((h) => {
                                                const isActive = sessionHours === h;
                                                const isSaving = savingHours === h;
                                                return (
                                                    <button
                                                        key={h}
                                                        type="button"
                                                        className={[
                                                            'pdd-session__opt',
                                                            isActive && 'pdd-session__opt--active',
                                                        ].filter(Boolean).join(' ')}
                                                        onClick={(e) => handleSessionHours(h, e)}
                                                        disabled={savingHours !== null}
                                                        aria-pressed={isActive}
                                                    >
                                                        {isSaving ? '…' : `${h}h`}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        <div className="pdd-session__hint">
                                            <FaInfoCircle aria-hidden="true" />
                                            <span>
                                                A nova duração vale a partir do seu <strong>próximo login</strong>.
                                                Sua sessão atual continua até o horário já definido.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {user.perfil === 'admin_geral' && (
    <div className="pdd-section">
        <p className="pdd-section__label">Testes & Dev</p>
        <div className="pdd-item" role="menuitem" onClick={handleSentryTest}>
            <FaBug aria-hidden="true" /> <span>Testar Sentry</span>
        </div>
    </div>
)}

                    </div>

                    {/* Logout */}
                    <div className="pdd-menu__logout">
                        <button className="pdd-logout-btn" onClick={onLogout} role="menuitem">
                            <FaSignOutAlt aria-hidden="true" /> <span>Sair</span>
                        </button>
                    </div>

                    {/* Rodapé: versão */}
                    <div className="pdd-menu__footer">
                        {isEditingVer ? (
                            <div className="pdd-version-edit">
                                <input
                                    className="pdd-version-edit__input"
                                    value={versionInput}
                                    onChange={(e) => setVersionInput(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label="Editar versão do sistema"
                                />
                                <button
                                    className="pdd-version-edit__btn pdd-version-edit__btn--save"
                                    onClick={handleVersionSave}
                                    title="Salvar"
                                    aria-label="Salvar versão"
                                >
                                    <FaSave />
                                </button>
                                <button
                                    className="pdd-version-edit__btn pdd-version-edit__btn--cancel"
                                    onClick={handleVersionCancel}
                                    title="Cancelar"
                                    aria-label="Cancelar edição"
                                >
                                    <FaTimes />
                                </button>
                            </div>
                        ) : (
                            <div className="pdd-version-display">
                                <span>Versão {appVersion}</span>
                                {user.perfil === 'admin_geral' && (
                                    <button
                                        className="pdd-version-display__edit"
                                        onClick={(e) => { e.stopPropagation(); setIsEditingVer(true); }}
                                        title="Editar versão"
                                        aria-label="Editar versão do sistema"
                                    >
                                        <FaEdit />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                </div>
            )}

            {/* Modais flutuantes */}
            {isCalcOpen && <Calculadora onClose={() => setIsCalcOpen(false)} />}
            
            {tempImage && (
                <ImageAdjustModal 
                    image={tempImage} 
                    isUploading={isUploading}
                    onCancel={() => { 
                        setTempImage(null); 
                        setRawFile(null); 
                    }}
                    onConfirm={handleUploadConfirm}
                />
            )}
        </div>
    );
}